/**
 * MS Teams {@link ChannelAdapter}: Bot Framework webhook ingress (JWT-verified,
 * fast-acked) + connector-service egress via {@link MsTeamsClient}. Raw fetch
 * only — no botbuilder. Identity stays with the engine: the adapter only tags
 * `isBot` mention entities and never resolves users itself (Decision 16).
 */
import type { AdapterContext, ChannelAdapter } from "../../src/adapter.js";
import type { CredentialPort, IdentityPort } from "../../src/ports.js";
import type {
  ActorQuery,
  ChannelActor,
  ConversationHealth,
  ConversationKey,
  InboundReaction,
  MessageRef,
  OutboundMessage,
  ReplyTarget,
  SurfaceCapabilities,
} from "../../src/types.js";
import { makeConversationKey, parseConversationKey } from "../../src/types.js";
import { makeQuestionCard, markdownToTeamsText } from "../../src/format/adaptive-card.js";
import type { TeamsActivity } from "./activity.js";
import {
  activityToChannelMessage,
  actorOfActivity,
  conversationKeyFromActivity,
  tenantIdOfActivity,
} from "./activity.js";
import { MsTeamsApiError, MsTeamsClient } from "./client.js";
import { validateBotFrameworkJwt } from "./jwt.js";

export * from "./activity.js";
export * from "./client.js";
export * from "./jwt.js";

/** Where the host mounts the Bot Framework messaging endpoint by default. */
const DEFAULT_INGRESS_PATH = "/channels/msteams/messages";

/** How long a `post` dedupe key retains its MessageRef (10 min). */
const DEDUPE_TTL_MS = 600_000;

/** Mention entities are only `isBot`-tagged here; the engine owns identity (Decision 16). */
const NULL_IDENTITY: IdentityPort = { resolveActor: async () => null };

/** A reaction-carrying activity; the extra arrays live outside {@link TeamsActivity}. */
type ReactionActivity = TeamsActivity & {
  reactionsAdded?: Array<{ type: string }>;
  reactionsRemoved?: Array<{ type: string }>;
};

/** Options for {@link MsTeamsAdapter}. */
export type MsTeamsAdapterOptions = {
  /** Skaile's multi-tenant Entra app id (non-secret; the secret comes via CredentialPort). */
  appId: string;
  /** Default "/channels/msteams/messages". */
  ingressPath?: string;
  openIdMetadataUrl?: string;
  loginBaseUrl?: string;
  fetchImpl?: typeof fetch;
};

/** What ingress remembers per conversation — egress needs both to post back. */
type ConversationInfo = { serviceUrl: string; tenantId?: string };

const emptyOk = (): Response => new Response(null, { status: 200 });
const jsonOk = (): Response =>
  new Response("{}", { status: 200, headers: { "content-type": "application/json" } });

/**
 * The MS Teams adapter (spec §6, plan Task 10). Webhook-style: `start` only
 * registers the ingress route — there is no outbound socket. Egress posting
 * requires the conversation to have been seen on ingress (serviceUrl + tenant
 * are remembered per conversationKey; Bot Framework provides no directory).
 */
export class MsTeamsAdapter implements ChannelAdapter {
  readonly platform = "msteams" as const;
  readonly capabilities: SurfaceCapabilities = {
    canEditMessages: true,
    canDeleteMessages: true,
    supportsThreads: true,
    supportsCards: true,
    supportsTyping: true,
    supportsEphemeral: false,
    supportsReactions: true,
    maxMessageLength: 25000,
    ackDeadlineMs: 15000,
    editThrottleMs: 1000,
    textFormat: "adaptive-card",
  };

  private readonly opts: MsTeamsAdapterOptions;
  private ctx?: AdapterContext;
  /** tenantId → lazily-created provider client (promise-cached to collapse races). */
  private readonly clients = new Map<string, Promise<MsTeamsClient>>();
  /** Ingress-observed conversation facts, keyed by conversationKey (thread AND channel level). */
  private readonly conversations = new Map<ConversationKey, ConversationInfo>();
  /** Ingress-observed actors by externalId — the lookupActor roster. */
  private readonly roster = new Map<string, ChannelActor>();
  /** post() idempotency: dedupeKey → prior ref, TTL {@link DEDUPE_TTL_MS}. */
  private readonly posted = new Map<string, { ref: MessageRef; expiresAt: number }>();

  constructor(opts: MsTeamsAdapterOptions) {
    this.opts = opts;
  }

  /** Registers the ingress route on the host; no connection is opened. */
  async start(ctx: AdapterContext): Promise<void> {
    this.ctx = ctx;
    ctx.ingress.registerHttpRoute?.(
      this.opts.ingressPath ?? DEFAULT_INGRESS_PATH,
      this.handleRequest,
    );
  }

  /** Drops the context and per-tenant clients; the host owns route teardown. */
  async stop(): Promise<void> {
    this.ctx = undefined;
    this.clients.clear();
  }

  /**
   * The Bot Framework messaging endpoint. Guarded by a `Promise.race` at
   * `ackDeadlineMs - 1000`: if processing overruns, Teams still gets its 200
   * and at-least-once redelivery covers the rest.
   */
  readonly handleRequest = async (req: Request): Promise<Response> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const guard = new Promise<Response>((resolve) => {
      timer = setTimeout(() => resolve(jsonOk()), this.capabilities.ackDeadlineMs - 1000);
    });
    try {
      return await Promise.race([this.processRequest(req), guard]);
    } finally {
      clearTimeout(timer);
    }
  };

  private async processRequest(req: Request): Promise<Response> {
    const ctx = this.ctx;
    if (!ctx) return new Response("adapter not started", { status: 503 });

    let activity: ReactionActivity;
    try {
      activity = (await req.json()) as ReactionActivity;
    } catch {
      return new Response("invalid JSON body", { status: 400 });
    }
    if (typeof activity?.type !== "string" || typeof activity?.serviceUrl !== "string") {
      return new Response("not a Bot Framework activity", { status: 400 });
    }

    const verdict = await validateBotFrameworkJwt(
      req.headers.get("authorization") ?? undefined,
      activity.serviceUrl,
      {
        appId: this.opts.appId,
        openIdMetadataUrl: this.opts.openIdMetadataUrl,
        fetchImpl: this.opts.fetchImpl,
      },
    );
    if (!verdict.ok) return new Response(verdict.reason, { status: 401 });

    this.rememberConversation(activity);
    this.rememberActors(activity);

    // Own-bot loop filter (spec §8) — before any sink call.
    if (activity.from?.id === activity.recipient?.id) return emptyOk();
    if (activity.type === "messageReaction") {
      await this.forwardReactions(activity, ctx);
      return emptyOk();
    }
    if (activity.type !== "message") return emptyOk();

    const msg = await activityToChannelMessage(activity, NULL_IDENTITY);
    if (!msg) return emptyOk();
    await ctx.sink.onMessage(msg);
    return jsonOk();
  }

  /** Records serviceUrl + tenant under the thread key AND its channel-level key. */
  private rememberConversation(a: TeamsActivity): void {
    const key = conversationKeyFromActivity(a);
    const info: ConversationInfo = { serviceUrl: a.serviceUrl, tenantId: tenantIdOfActivity(a) };
    this.conversations.set(key, info);
    const { conversationId } = parseConversationKey(key);
    this.conversations.set(makeConversationKey("msteams", conversationId), info);
  }

  /** Grows the lookupActor roster from the sender and mention entities (bot excluded). */
  private rememberActors(a: TeamsActivity): void {
    if (a.from?.id !== a.recipient?.id) {
      const actor = actorOfActivity(a);
      if (actor.externalId) this.roster.set(actor.externalId, actor);
    }
    for (const e of a.entities ?? []) {
      if (e.type !== "mention" || !e.mentioned || e.mentioned.id === a.recipient?.id) continue;
      this.roster.set(e.mentioned.id, {
        platform: "msteams",
        externalId: e.mentioned.id,
        displayName: e.mentioned.name ?? "",
        tenantId: tenantIdOfActivity(a),
      });
    }
  }

  /** Maps a messageReaction activity onto InboundReaction events (one per emoji). */
  private async forwardReactions(a: ReactionActivity, ctx: AdapterContext): Promise<void> {
    if (!ctx.sink.onReaction) return;
    const conversationKey = conversationKeyFromActivity(a);
    const actor = actorOfActivity(a);
    const messageRef: MessageRef = {
      platform: "msteams",
      conversationKey,
      // Teams points a reaction at its target via replyToId.
      messageId: a.replyToId ?? a.id,
      raw: { serviceUrl: a.serviceUrl, conversationId: a.conversation.id },
    };
    const groups: Array<[InboundReaction["action"], Array<{ type: string }>]> = [
      ["added", a.reactionsAdded ?? []],
      ["removed", a.reactionsRemoved ?? []],
    ];
    for (const [action, reactions] of groups) {
      for (const reaction of reactions) {
        await ctx.sink.onReaction({
          conversationKey,
          messageRef,
          emoji: reaction.type,
          actor,
          action,
          eventId: `${a.id}:${action}:${reaction.type}`,
        });
      }
    }
  }

  /** Posts a message; a repeated `dedupeKey` returns the prior ref with no second side effect. */
  async post(target: ReplyTarget, msg: OutboundMessage, dedupeKey: string): Promise<MessageRef> {
    const prior = this.dedupeGet(dedupeKey);
    if (prior) return prior;
    if (msg.markdown.length > this.capabilities.maxMessageLength) {
      throw new Error(
        `message length ${msg.markdown.length} exceeds maxMessageLength ${this.capabilities.maxMessageLength} — chunk upstream`,
      );
    }
    const { serviceUrl, conversationId } = this.resolveTarget(target);
    const client = await this.clientForConversation(target.conversationKey);
    const { id } = await client.postActivity(serviceUrl, conversationId, this.buildActivity(msg));
    const ref: MessageRef = {
      platform: "msteams",
      conversationKey: target.conversationKey,
      messageId: id,
      raw: { serviceUrl, conversationId },
    };
    this.posted.set(dedupeKey, { ref, expiresAt: Date.now() + DEDUPE_TTL_MS });
    return ref;
  }

  /** Replaces a previously posted activity's content in place. */
  async update(ref: MessageRef, msg: OutboundMessage): Promise<void> {
    const raw = (ref.raw ?? {}) as Partial<ConversationInfo & { conversationId: string }>;
    const fallback =
      typeof raw.serviceUrl === "string" && typeof raw.conversationId === "string"
        ? { serviceUrl: raw.serviceUrl, conversationId: raw.conversationId }
        : this.resolveTarget({ conversationKey: ref.conversationKey });
    const client = await this.clientForConversation(ref.conversationKey);
    await client.updateActivity(
      fallback.serviceUrl,
      fallback.conversationId,
      ref.messageId,
      this.buildActivity(msg),
    );
  }

  /** Native Teams typing indicator — fire-and-forget, never deduped. */
  async indicateTyping(target: ReplyTarget): Promise<void> {
    const { serviceUrl, conversationId } = this.resolveTarget(target);
    const client = await this.clientForConversation(target.conversationKey);
    await client.postActivity(serviceUrl, conversationId, { type: "typing" });
  }

  /** Preflight: a typing probe proves the bot can post; 403/404 → actionable problem. */
  async verifyConversation(key: ConversationKey): Promise<ConversationHealth> {
    try {
      const { serviceUrl, conversationId } = this.resolveTarget({ conversationKey: key });
      const client = await this.clientForConversation(key);
      await client.postActivity(serviceUrl, conversationId, { type: "typing" });
      return { ok: true, problems: [] };
    } catch (err) {
      if (err instanceof MsTeamsApiError && (err.status === 403 || err.status === 404)) {
        return {
          ok: false,
          problems: [
            `bot cannot post to ${key} — check the app is installed in the team and the bot has access`,
          ],
        };
      }
      return { ok: false, problems: [err instanceof Error ? err.message : String(err)] };
    }
  }

  /** Resolves from the ingress-observed roster only (Graph lookup is platform glue, later). */
  async lookupActor(query: ActorQuery): Promise<ChannelActor | undefined> {
    if (query.externalId) return this.roster.get(query.externalId);
    for (const actor of this.roster.values()) {
      if (query.email && actor.email === query.email) return actor;
      if (query.name && actor.displayName.toLowerCase().includes(query.name.toLowerCase())) {
        return actor;
      }
    }
    return undefined;
  }

  /** Decision 11: Adaptive Card for questions with options; markdown text otherwise. */
  private buildActivity(msg: OutboundMessage): Record<string, unknown> {
    if (msg.kind === "question" && msg.options?.length) {
      return {
        type: "message",
        attachments: [
          {
            contentType: "application/vnd.microsoft.card.adaptive",
            content: makeQuestionCard(markdownToTeamsText(msg.markdown), msg.options),
          },
        ],
      };
    }
    const text = markdownToTeamsText(msg.markdown);
    return {
      type: "message",
      textFormat: "markdown",
      text: msg.kind === "error" ? `⚠️ ${text}` : text,
    };
  }

  /** serviceUrl + native conversation id for a target — threadRef.raw first, ingress memory else. */
  private resolveTarget(target: ReplyTarget): { serviceUrl: string; conversationId: string } {
    const raw = (target.threadRef?.raw ?? {}) as { serviceUrl?: unknown; conversationId?: unknown };
    if (typeof raw.serviceUrl === "string" && typeof raw.conversationId === "string") {
      return { serviceUrl: raw.serviceUrl, conversationId: raw.conversationId };
    }
    const { conversationId, threadRootId } = parseConversationKey(target.conversationKey);
    const nativeId = threadRootId ? `${conversationId};messageid=${threadRootId}` : conversationId;
    const info = this.infoFor(target.conversationKey);
    if (!info) {
      throw new Error(
        `no known serviceUrl for ${target.conversationKey} — the conversation has not been seen on ingress`,
      );
    }
    return { serviceUrl: info.serviceUrl, conversationId: nativeId };
  }

  /** Conversation facts under the exact key, falling back to its channel-level key. */
  private infoFor(key: ConversationKey): ConversationInfo | undefined {
    const exact = this.conversations.get(key);
    if (exact) return exact;
    const { conversationId } = parseConversationKey(key);
    return this.conversations.get(makeConversationKey("msteams", conversationId));
  }

  /** One MsTeamsClient per tenant, built from CredentialPort (kind must be "msteams"). */
  private clientForConversation(key: ConversationKey): Promise<MsTeamsClient> {
    const ctx = this.ctx;
    if (!ctx) return Promise.reject(new Error("adapter not started"));
    const tenantId = this.infoFor(key)?.tenantId;
    if (!tenantId) {
      return Promise.reject(
        new Error(`no known tenant for ${key} — the conversation has not been seen on ingress`),
      );
    }
    return this.clientForTenant(tenantId, ctx.credentials);
  }

  private clientForTenant(tenantId: string, credentials: CredentialPort): Promise<MsTeamsClient> {
    const cached = this.clients.get(tenantId);
    if (cached) return cached;
    const created = (async () => {
      const cred = await credentials.forTenant("msteams", tenantId);
      if (cred.kind !== "msteams") {
        throw new Error(
          `expected "msteams" credentials for tenant ${tenantId}, got "${cred.kind}"`,
        );
      }
      return new MsTeamsClient({
        appId: cred.appId,
        clientSecret: cred.clientSecret,
        loginBaseUrl: this.opts.loginBaseUrl,
        fetchImpl: this.opts.fetchImpl,
      });
    })();
    this.clients.set(tenantId, created);
    // A failed credential resolution must not be cached forever.
    created.catch(() => this.clients.delete(tenantId));
    return created;
  }

  /** Live dedupe hit for `key`, sweeping expired entries opportunistically. */
  private dedupeGet(key: string): MessageRef | undefined {
    const now = Date.now();
    for (const [k, entry] of this.posted) {
      if (entry.expiresAt <= now) this.posted.delete(k);
    }
    return this.posted.get(key)?.ref;
  }
}
